import { createAsyncThunk,createSlice } from '@reduxjs/toolkit';
import { jwtDecode } from 'jwt-decode';
import { toast } from 'react-toastify';

import api from '../../api';

// Check if token exists and is valid (not expired)
const checkToken = () => {
  const token = localStorage.getItem('token');
  const SECS_TO_MILIS = 1000;
  if (!token) return false;

  try {
    const decoded = jwtDecode(token);
    if (decoded.exp * SECS_TO_MILIS < Date.now()) {
      localStorage.removeItem('token');
      return false;
    }
    return true;
  } catch (error) {
    localStorage.removeItem('token');
    return false;
  }
};

const initialTokenCheck = checkToken();

const retryRequest = async (requestFn, maxRetries = 2, delay = 500) => {
  for (let i = 0; i <= maxRetries; i++) {
    try {
      return await requestFn();
    } catch (error) {
      if (i === maxRetries) throw error;
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
};

export const getCurrentUser = createAsyncThunk(
  'auth/getCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const res = await retryRequest(async () => {
        const response = await api.get(`/myself`);

        // Check if response is properly formatted
        if (!response || !response.data || !response.data.data) {
          throw new Error('Invalid response format from server');
        }

        return response;
      });

      return res.data.data;
    } catch (error) {
      // Handle case where response is undefined or malformed
      if (!error.response && !error.message) {
        const errorMessage = 'Network error or malformed response from server';
        return rejectWithValue(errorMessage);
      }

      const errorMessage =
        error?.error ||
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        error?.errors?.[0]?.message ||
        'Something went wrong trying to get your info.';
      return rejectWithValue(errorMessage);
    }
  }
);

export const checkAuthStatus = createAsyncThunk(
  'auth/checkAuthStatus',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      if (initialTokenCheck) {
        await dispatch(getCurrentUser());
        return true;
      }
      return rejectWithValue('No initial token available');
    } catch (error) {
      return rejectWithValue(error.message || 'Authentication check failed');
    }
  }
);

export const loginRequest = createAsyncThunk(
  'auth/loginRequest',
  async ({ email, password }, { dispatch, rejectWithValue }) => {
    try {
      const res = await api.post('auth/login', { email, password });

      const { accessToken, user } = res.data.data;
      if (accessToken) {
        await dispatch(getCurrentUser());
      }

      toast.success(`Welcome back!`);
      return res.data.data;
    } catch (error) {
      toast.error(error?.errors?.[0]?.message);
      const errorMessage =
        error?.error ||
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        error?.errors?.[0]?.message ||
        'Something went wrong while logging in.';

      return rejectWithValue(errorMessage);
    }
  }
);

const initialState = {
  user: null,
  isAuthenticated: initialTokenCheck,
  loading: false,
  initialAuthCheckDone: false,
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logoutRequested: (state) => {
      localStorage.removeItem('token');
      state.isAuthenticated = false;
      state.user = null;
    },
    updateUser: (state, action) => {
      state.user = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(checkAuthStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkAuthStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.initialAuthCheckDone = true;
        state.isAuthenticated = action.payload;
      })
      .addCase(checkAuthStatus.rejected, (state) => {
        state.loading = false;
        state.initialAuthCheckDone = true;
        state.isAuthenticated = false;
      })
      .addCase(getCurrentUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.loading = false;
        state.isAuthenticated = true;
      })
      .addCase(getCurrentUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
        state.user = null;
      })
      .addCase(loginRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginRequest.fulfilled, (state) => {
        state.loading = false;
        state.isAuthenticated = true;
      })
      .addCase(loginRequest.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.error = action.payload;
      });
  },
});

export const { logoutRequested, updateUser, clearError } = authSlice.actions;

export default authSlice.reducer;
